/**
 * FreightQuoteModal — Modal for selecting a freight carrier with live quotes.
 *
 * Shows live freight quotes from the Sage freight API, sorted cheapest-first.
 * The customer's recommended carrier (from their Sage ship-to address) appears
 * at the top with a "Recommended" badge.
 */

import { useState, useEffect, useCallback } from 'react';
import { portalOrderService } from '../../services/portalOrders';
import type {
  FreightDataIssue,
  FreightQuoteItem,
  FreightQuotesResponse,
  FreightRequestSummary,
} from '../../services/portalOrders';
import styles from './FreightQuoteModal.module.css';

interface FreightQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (carrierCode: string) => void;
  orderId: string;
  currentShipVia: string | null;
  /**
   * Called when the CSR clicks "Mark as truckload" in the empty/all-carriers-
   * refused state. The parent should record the truckload-pending intent
   * via Sage's `CancelReasonCode='FRT'` hold ("Customer frt chg sign off"),
   * close the modal, and let Sage track the state from there. The modal
   * stays agnostic to where the flag lives.
   */
  onMarkTruckload?: () => void;
  /**
   * Whether the order is currently flagged as truckload-pending. The parent
   * derives this from `order.cancel_reason_code === 'FRT'`; the modal just
   * renders the active confirmation when true.
   */
  isTruckloadPending?: boolean;
  /**
   * View-only mode for orders past Draft: quotes are shown for lookup but
   * carriers can't be selected (the carrier is already locked into Sage) and
   * the truckload affordance is hidden.
   */
  readOnly?: boolean;
}

export default function FreightQuoteModal({
  isOpen,
  onClose,
  onSelect,
  orderId,
  currentShipVia,
  onMarkTruckload,
  isTruckloadPending = false,
  readOnly = false,
}: FreightQuoteModalProps) {
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<FreightQuotesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const result = await portalOrderService.getFreightQuotes(orderId);
      setResponse(result);
      if (!result.success && result.error_message) {
        setError(result.error_message);
      }
    } catch (err) {
      setError('Failed to fetch freight quotes. Please try again.');
      console.error('Freight quote fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (isOpen) {
      fetchQuotes();
    }
  }, [isOpen, fetchQuotes]);

  if (!isOpen) return null;

  // The backend resolves each freight-API carrier to its canonical Sage
  // ShippingCode and sends it as `ship_via_code`. Pass that through
  // verbatim — do NOT uppercase or modify the freight key (doing so
  // produced invalid codes like "RL" for R&L until 2026-04-22).
  const handleSelect = (shipViaCode: string) => {
    onSelect(shipViaCode);
    onClose();
  };

  // Sort quotes: recommended first, then by price ascending, errors last
  const sortedQuotes = response?.quotes
    ? [...response.quotes].sort((a, b) => {
        // Recommended always first
        if (a.is_recommended && !b.is_recommended) return -1;
        if (!a.is_recommended && b.is_recommended) return 1;
        // Successful quotes before errors
        if (a.rate !== null && b.rate === null) return -1;
        if (a.rate === null && b.rate !== null) return 1;
        // Sort by price ascending
        if (a.rate !== null && b.rate !== null) return a.rate - b.rate;
        return 0;
      })
    : [];

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>
            {readOnly ? 'Freight Quotes' : 'Select Freight Carrier'}
          </h3>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
            data-action="order_freight_quote_close"
            data-target-order-id={orderId}
            data-outcome="close"
          >
            &times;
          </button>
        </div>

        <div className={styles.body}>
          {loading && (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <p>Fetching live freight quotes...</p>
              <p className={styles.loadingHint}>This may take up to 30 seconds</p>
            </div>
          )}

          {error && !loading && (
            <div className={styles.errorState}>
              <p className={styles.errorMessage}>{error}</p>
              <button className={styles.retryBtn} onClick={fetchQuotes}>
                Retry
              </button>
            </div>
          )}

          {!loading && !error && sortedQuotes.length === 0 && (
            <div className={styles.emptyState}>
              <p>No freight quotes available for this order.</p>
              <p className={styles.emptyHint}>
                Ensure the order has a valid ship-to zip code and line items.
              </p>
            </div>
          )}

          {!loading && sortedQuotes.length > 0 && (
            <>
              {response?.issues && response.issues.length > 0 && (
                <IssueBanner issues={response.issues} />
              )}
              {response?.latency_ms != null && response.latency_ms > 0 && (
                <p className={styles.latencyNote}>
                  Quotes fetched in {(response.latency_ms / 1000).toFixed(1)}s
                </p>
              )}
              <div className={styles.quoteList}>
                {sortedQuotes.map((quote) => (
                  <QuoteRow
                    key={quote.ship_via_code}
                    orderId={orderId}
                    quote={quote}
                    isSelected={
                      currentShipVia?.toUpperCase() ===
                      quote.ship_via_code.toUpperCase()
                    }
                    onSelect={handleSelect}
                    readOnly={readOnly}
                  />
                ))}
              </div>
              {response?.request_summary && (
                <RequestSummaryPanel summary={response.request_summary} />
              )}
            </>
          )}

          {/*
           * Truckload affordance: render whenever the API returned a
           * carrier_none signal (zero LTL quotes — likely TL territory).
           * Hadi 2026-05-04: "the freight API itself should be sufficient
           * to tell us." We let that signal drive the UX.
           */}
          {!loading
            && !readOnly
            && response?.issues?.some((i) => i.code === 'carrier_none')
            && onMarkTruckload && (
            <div
              className={
                isTruckloadPending
                  ? `${styles.truckloadBox} ${styles.truckloadBoxActive}`
                  : styles.truckloadBox
              }
              role="region"
              aria-label="Truckload action"
            >
              {isTruckloadPending ? (
                <p className={styles.truckloadActiveMsg}>
                  This order is marked as <strong>truckload-pending</strong>.
                  Submit without an LTL carrier; ops will attach the TL quote
                  separately.
                </p>
              ) : (
                <>
                  <p className={styles.truckloadHint}>
                    No LTL carrier can quote this shipment. Mark the order as
                    truckload-pending to submit without an LTL carrier — ops
                    will attach a separately-quoted TL rate.
                  </p>
                  <button
                    type="button"
                    className={styles.markTruckloadBtn}
                    data-action="freight_mark_truckload_pending"
                    data-outcome="request"
                    onClick={() => {
                      onMarkTruckload();
                      onClose();
                    }}
                  >
                    Mark as truckload-pending
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// QuoteRow — a single carrier quote
// ---------------------------------------------------------------------------

function QuoteRow({
  quote,
  isSelected,
  onSelect,
  orderId,
  readOnly = false,
}: {
  quote: FreightQuoteItem;
  isSelected: boolean;
  onSelect: (shipViaCode: string) => void;
  orderId?: string;
  readOnly?: boolean;
}) {
  const hasRate = quote.rate !== null;
  const hasBreakdown = quote.freight_charge !== null && quote.fuel_surcharge !== null;
  // Prefer the resolved Sage ShippingCode for display — it's the actual
  // carrier identifier the user is selecting (e.g. "R&L" vs "RL").
  const carrierLabel = quote.ship_via_code;

  return (
    <button
      className={`${styles.quoteRow} ${isSelected ? styles.quoteRowSelected : ''} ${
        !hasRate ? styles.quoteRowDisabled : ''
      } ${readOnly ? styles.quoteRowReadOnly : ''}`}
      onClick={() => !readOnly && hasRate && onSelect(quote.ship_via_code)}
      disabled={!hasRate || readOnly}
      type="button"
      // One recorder step per carrier quote picked, the carrier and its rate
      // as the chosen value instead of the whole label (kanban 68b23ba8).
      data-action="carrier_quote_pick"
      data-record-value="carrier"
      value={hasRate ? `${quote.ship_via_code} ${quote.rate!.toFixed(2)}` : quote.ship_via_code}
      data-target-order-id={orderId}
    >
      <div className={styles.quoteCarrier}>
        <span className={styles.carrierName}>{carrierLabel}</span>
        {quote.is_recommended && (
          <span className={styles.recommendedBadge}>Recommended</span>
        )}
        {isSelected && <span className={styles.selectedBadge}>Current</span>}
        {quote.transit_days !== null && (
          <span className={styles.transitBadge}>
            {quote.transit_days} {quote.transit_days === 1 ? 'day' : 'days'}
          </span>
        )}
      </div>
      <div className={styles.quotePrice}>
        {hasRate ? (
          <div className={styles.priceBlock}>
            <span className={styles.priceValue}>
              ${quote.rate!.toFixed(2)}
            </span>
            {hasBreakdown && (
              <div className={styles.priceBreakdown}>
                <span>Freight: ${quote.freight_charge!.toFixed(2)}</span>
                <span>Fuel: ${quote.fuel_surcharge!.toFixed(2)}</span>
              </div>
            )}
            {quote.delivery_date && (
              <span className={styles.deliveryDate}>
                Est. {quote.delivery_date}
              </span>
            )}
          </div>
        ) : (
          <span className={styles.priceError}>{quote.error || 'Unavailable'}</span>
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// IssueBanner — salesperson-visible data-quality warnings/errors
// ---------------------------------------------------------------------------
//
// Shown ABOVE the quote list when the backend detects sanity issues
// (weight off a cliff, 0 skids, hazmat catalog mismatch, etc.). Errors
// get a red style; warnings get an amber style. No role gating — every
// user sees these because they can affect which carrier is the right
// choice.

function IssueBanner({ issues }: { issues: FreightDataIssue[] }) {
  const hasError = issues.some((i) => i.severity === 'error');
  const bannerClass = hasError
    ? `${styles.issueBanner} ${styles.issueBannerError}`
    : `${styles.issueBanner} ${styles.issueBannerWarning}`;

  return (
    <div className={bannerClass} role="alert">
      {issues.map((issue) => (
        <div key={issue.code} className={styles.issueRow}>
          <span
            className={
              issue.severity === 'error'
                ? styles.issueIconError
                : styles.issueIconWarning
            }
            aria-hidden="true"
          >
            {issue.severity === 'error' ? '✕' : '⚠'}
          </span>
          <span className={styles.issueMessage}>{issue.message}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RequestSummaryPanel — Sage's resolved shipment details
// ---------------------------------------------------------------------------
//
// The user-facing fields (address, weights, skids, hazmat/freezable flags)
// are populated for every portal user — the panel renders for everyone
// once the API has a request envelope. The two admin-only debug fields
// (``attempts``/``http_status``) are populated only for admins, so the
// debug subline below acts as the admin-mode signal: when those fields
// are non-null the panel also shows the "admin" badge.

function RequestSummaryPanel({ summary }: { summary: FreightRequestSummary }) {
  const isAdminView =
    summary.attempts != null || summary.http_status != null;
  const city = summary.shipping_city;
  const state = summary.shipping_state;
  const zip = summary.shipping_zip;
  const dest = [city, state].filter(Boolean).join(', ');
  const destLine = dest && zip ? `${dest} ${zip}` : dest || zip || '—';

  const weightLine =
    summary.total_weight != null
      ? `${summary.total_weight.toFixed(0)} lb` +
        (summary.product_weight != null && summary.skid_weight != null
          ? ` (${summary.product_weight.toFixed(0)} product + ${summary.skid_weight.toFixed(0)} skid)`
          : '')
      : '—';

  const skidLine =
    summary.total_skids != null
      ? `${summary.total_skids} ${summary.total_skids === 1 ? 'skid' : 'skids'}`
      : '—';

  return (
    <div className={styles.requestSummary}>
      <div className={styles.requestSummaryHeader}>
        <span className={styles.requestSummaryTitle}>Sage saw</span>
        {isAdminView && (
          <span className={styles.requestSummaryBadge}>admin</span>
        )}
      </div>
      <dl className={styles.requestSummaryGrid}>
        <dt>Destination</dt>
        <dd>{destLine}</dd>
        <dt>Weight</dt>
        <dd>{weightLine}</dd>
        <dt>Skids</dt>
        <dd>{skidLine}</dd>
        {(summary.has_hazmat_items || summary.has_freezable_items) && (
          <>
            <dt>Flags</dt>
            <dd>
              {summary.has_hazmat_items && (
                <span className={styles.flagHazmat}>Hazmat</span>
              )}
              {summary.has_freezable_items && (
                <span className={styles.flagFreezable}>Freezable</span>
              )}
            </dd>
          </>
        )}
      </dl>
      {(summary.attempts != null || summary.http_status != null) && (
        <p className={styles.requestSummaryDebug}>
          {summary.attempts != null && (
            <span>
              {summary.attempts} {summary.attempts === 1 ? 'attempt' : 'attempts'}
            </span>
          )}
          {summary.attempts != null && summary.http_status != null && (
            <span> · </span>
          )}
          {summary.http_status != null && <span>HTTP {summary.http_status}</span>}
        </p>
      )}
    </div>
  );
}
